import React, { createContext, useContext, ReactNode } from 'react';
import { observable, action, computed } from 'mobx';
import { observer } from 'mobx-react';
import { ILogger, IStreamingProviderFactory } from '@honeybook/core';
import { IChatConversation } from '@honeybook/ai-chat';
import { PRIORITY_LIST_LOGICAL_ID_PREFIX } from '@honeybook/ai-chat/consts';

interface IConversationUpdateEvent {
    event: 'conversation_updated';
    data: IChatConversation;
}

export class ConversationsListStore {
    @observable conversations: IChatConversation[] = [];

    @observable isConversationsInitialized: boolean = false;

    @observable selectedConversationId: string | null = null;

    @observable isLoadingConversations: boolean = false;

    @observable isStreamingConnected: boolean = false;
    @observable isConnecting: boolean = false;

    @observable companyId: string | null = null;

    @action.bound
    setCompanyId(companyId: string) {
        if (this.companyId !== companyId) {
            this.companyId = companyId;
            this.resetState();
        }
    }

    @action.bound
    resetState() {
        this.setConversations([]);
        this.setIsConversationsInitialized(false);
        this.setSelectedConversationId(null);
        this.setIsStreamingConnected(false);
        this.unlockStreaming();
    }

    @action.bound
    setSelectedConversationId(conversationId: string | null) {
        this.selectedConversationId = conversationId;
    }

    @action.bound
    setIsConversationsInitialized(isInitialized: boolean) {
        this.isConversationsInitialized = isInitialized;
    }

    @action.bound
    setIsLoadingConversations(isLoading: boolean) {
        this.isLoadingConversations = isLoading;
    }

    @action.bound
    setConversations(conversations: IChatConversation[]) {
        this.conversations = conversations;
    }

    @action.bound
    addConversation(conversation: IChatConversation) {
        const currentConversations = this.conversations || [];
        this.setConversations([...currentConversations, conversation]);
    }

    @action.bound
    shouldFetchConversations(): boolean {
        // Prevent multiple simultaneous fetches
        if (this.isLoadingConversations || this.isConversationsInitialized) {
            return false;
        }
        this.setIsLoadingConversations(true);
        return true;
    }

    @action.bound
    setIsStreamingConnected(isStreamingConnected: boolean) {
        this.isStreamingConnected = isStreamingConnected;
    }

    @action.bound
    tryLockStreaming(): boolean {
        if (this.isStreamingConnected || this.isConnecting) {
            return false;
        }
        this.isConnecting = true;
        return true;
    }

    @action.bound
    unlockStreaming() {
        this.isConnecting = false;
    }

    @action.bound
    handleConversationUpdate = (socketEvent: IConversationUpdateEvent) => {
        if (socketEvent.event === 'conversation_updated') {
            const currentConversations = this.conversations || [];
            const newConversations = currentConversations.map(conversation =>
                conversation._id === socketEvent.data._id
                    ? socketEvent.data
                    : conversation
            );

            this.setConversations(newConversations);
        }
    };

    @action.bound
    async initStreamingIfNeeded({
        streamingProviderFactory,
        userId,
        companyId,
        logger
    }: {
        streamingProviderFactory: IStreamingProviderFactory;
        userId: string;
        companyId: string;
        logger: ILogger;
    }) {
        if (!this.tryLockStreaming()) {
            return;
        }

        try {
            const provider = await streamingProviderFactory.createProvider({
                type: 'websocket',
                data: {
                    room_id: `${companyId}.ai_chat_conversations`,
                    user_id: userId
                }
            });

            // Start streaming provider
            await provider.start();

            // Handle conversation updates
            (async () => {
                for await (const update of provider.stream()) {
                    const socketEvent = update as IConversationUpdateEvent;
                    this.handleConversationUpdate(socketEvent);
                }
            })();

            this.setIsStreamingConnected(true);
        } catch (error) {
            logger.error('Failed to start streaming provider', error);
            this.setIsStreamingConnected(false);
        } finally {
            this.unlockStreaming();
        }
    }

    @computed
    get priorityListConversation() {
        return this.conversations.find(conversation =>
            conversation.logical_id?.startsWith(PRIORITY_LIST_LOGICAL_ID_PREFIX)
        );
    }
}

export const conversationsListStore = new ConversationsListStore();

const ConversationsListContext = createContext<ConversationsListStore>(
    conversationsListStore
);

interface ConversationsListProviderProps {
    children: ReactNode;
    value: ConversationsListStore;
}

export const ConversationsListProvider = observer(
    ({ children, value }: ConversationsListProviderProps) => {
        return (
            <ConversationsListContext.Provider value={value}>
                {children}
            </ConversationsListContext.Provider>
        );
    }
);

export const useConversationsList = () => {
    const context = useContext(ConversationsListContext);
    if (!context) {
        throw new Error(
            'useConversationsList must be used within a ConversationsListProvider'
        );
    }

    return context;
};
